import type { DbMeetingAttendee } from "@/types/db";

/**
 * Snapshot of every user-editable field that feeds meeting minutes
 * generation, captured when a draft generation is kicked off and committed
 * as the baseline once that generation succeeds. When the user clicks
 * Retry (re-draft) the live drafts are compared against this baseline so an
 * unchanged meeting can skip regeneration entirely — no edge function call,
 * no token spend.
 *
 * Values are normalised on capture so cursor-position-level noise never
 * counts as a change: strings are trimmed, and attendees are reduced to
 * their identity — `user_id` for internal attendees, case-insensitive
 * trimmed name for external ones (mirroring `mergeMeetingAttendees`) —
 * deduplicated and sorted so reordering is not a change either.
 *
 * The meeting title is deliberately excluded: generation only uses it as a
 * hint and overwrites it with a generated title, so including it would make
 * every successful draft immediately look "changed".
 */
export type MeetingDraftSnapshot = {
  meetingContext: string;
  meetingInstructions: string;
  attendees: string[];
  previousMeetingId: string | null;
  previousMeetingNotes: string;
  layoutId: string | null;
};

export type MeetingDraftSnapshotInput = {
  meetingContext: string;
  meetingInstructions: string;
  attendees: DbMeetingAttendee[];
  previousMeetingId: string | null;
  previousMeetingNotes: string;
  layoutId: string | null;
};

function attendeeIdentityKey(attendee: DbMeetingAttendee): string {
  return attendee.user_id
    ? `user:${attendee.user_id}`
    : `name:${attendee.name.trim().toLowerCase()}`;
}

export function createMeetingDraftSnapshot(
  input: MeetingDraftSnapshotInput
): MeetingDraftSnapshot {
  const attendeeKeys = Array.from(
    new Set(
      input.attendees
        .filter((attendee) => Boolean(attendee.user_id) || Boolean(attendee.name.trim()))
        .map(attendeeIdentityKey)
    )
  ).sort();

  return {
    meetingContext: input.meetingContext.trim(),
    meetingInstructions: input.meetingInstructions.trim(),
    attendees: attendeeKeys,
    previousMeetingId: input.previousMeetingId,
    previousMeetingNotes: input.previousMeetingNotes.trim(),
    layoutId: input.layoutId,
  };
}

export function isMeetingDraftSnapshotEqual(
  a: MeetingDraftSnapshot,
  b: MeetingDraftSnapshot
): boolean {
  return (
    a.meetingContext === b.meetingContext &&
    a.meetingInstructions === b.meetingInstructions &&
    a.previousMeetingId === b.previousMeetingId &&
    a.previousMeetingNotes === b.previousMeetingNotes &&
    a.layoutId === b.layoutId &&
    a.attendees.length === b.attendees.length &&
    a.attendees.every((key, index) => key === b.attendees[index])
  );
}
